import {
  queriesInternalAccounts,
  queriesPayrollRuns,
  queriesWorkLogs
} from "@query";
import {
  toWSDateString,
  WSAlert,
  WSElement,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPill,
  WSSectionToolbar,
  WSTabs
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  ListAuditLogsFilterParameterResourceTypeEq,
  PayrollRunCycleType,
  PayrollRunExpand,
  PayrollRunStatus,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { useHistory } from "react-router-dom";

import { WSQueries } from "../../../../query/WSQuery";
import { ResourceHistory } from "../../../../shared/components/ResourceHistory";
import { useModalResourceDrillDownHistory } from "../../../../shared/components/ResourceHistory/ModalResourceDrillDownHistory";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { PATH_SETTINGS_PAYROLL } from "../../../Settings/paths";
import { PATH_WALLET } from "../../../Wallet/paths";
import { routeHelpersWorkLogPayerDetails } from "../../../WorkLogs/paths";
import { PAYROLL_CURRENCY } from "../../constants";
import {
  useModalCancelPayroll,
  useModalDeletePayroll,
  useModalFinalizePayroll
} from "../../modals/PayrollRunActionModals";
import { routeHelperPayrollRunsPaystub } from "../../paths";
import { selectorPayrollRunApprovalSteps } from "../../selectors/selectorPayrollRunApprovalSteps";
import { selectorPayrollRunBreakdownAmounts } from "../../selectors/selectorPayrollRunBreakdownAmounts";
import { selectorPayrollRunNumber } from "../../selectors/selectorPayrollRunNumber";
import { selectorPayrollRunWorklogApproval } from "../../selectors/selectorPayrollRunWorklogApproval";
import { selectorPayrollRunsWalletBalance } from "../../selectors/selectorPayrollRunsWalletBalance";
import { formatPayPeriodLabel, getPayrollRunYear } from "../../utils";
import { ApprovalWindowWidget } from "../ApprovalWindowWidget";
import { PayrollRunDetailsCard } from "../PayrollRunDetailsCard";
import { FilterableTablePaystubs } from "../TablePaystubs/FilterableTablePaystubs";
import { TableRunWorkLogs } from "../TableRunWorkLogs/TableRunWorkLogs";
import { WalletBalanceCard } from "../WalletBalanceCard";

type ToolbarBreadcrumbs = NonNullable<
  React.ComponentProps<typeof WSPageToolbar>["breadcrumbs"]
>;
type ToolbarAction = NonNullable<
  React.ComponentProps<typeof WSPageToolbar>["actions"]
>[number];

type Props = {
  payrollRunId: string;
  /** Crumbs of the surface this run was opened from; the run's own is appended. */
  breadcrumbs: ToolbarBreadcrumbs;
  /** Where to land once the run is deleted — it no longer exists. */
  onDeleted: () => void;
};

/**
 * One employee payroll run, to the new payroll design: the funding card, the
 * approval window, the Wingspan wallet balance that funds it, and the work logs
 * and pay statements it pays out.
 *
 * Contractor runs are a different screen (`PayrollRunDetailsContractor`), which
 * keeps the retired v1 Payables layout. Nothing is shared between the two but
 * the `getPayrollRun` read — they answer different questions for different
 * payers, and folding them into one component with a `type` prop made both
 * worse.
 */
export const PayrollRunDetailsEmployee: React.FC<Props> = props => {
  const { payrollRunId } = props;
  const history = useHistory();

  const goToPaystub = routeHelperPayrollRunsPaystub.useGoTo();
  const goToWorkLogDetails = routeHelpersWorkLogPayerDetails.useGoTo();
  const modalFinalizePayroll = useModalFinalizePayroll();
  const modalCancelPayroll = useModalCancelPayroll();
  const modalDeletePayroll = useModalDeletePayroll();
  const modalResourceDrillDownHistory = useModalResourceDrillDownHistory();

  // Read from the prop rather than `payrollRun.id`, which the v3 model types as
  // optional.
  const payrollRunResource = {
    resourceId: payrollRunId,
    resourceType: ListAuditLogsFilterParameterResourceTypeEq.PAYROLL_RUN
  };

  const queryPayroll = queriesPayrollRuns.getPayrollRun.useQuery({
    payrollRunId,
    expand: [PayrollRunExpand.BREAKDOWN]
  });

  // Secondary queries stay OUTSIDE `WSQueries` (as unconditional hooks) — they
  // are `enabled`-gated on the primary query, so they never become `isSuccess`
  // on their own and would otherwise keep `WSQueries` stuck on the loader. Their
  // data is consumed with graceful fallbacks in the render prop below.
  //
  // Funding wallet + balance — same pattern as RoutePayrollRunsActivePayrolls.
  // Assumption: every employee run is funded from the Wingspan wallet; the v3
  // API exposes no per-run funding-source field to confirm this.
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const fundingAccount = queryInternalAccounts.data?.data?.find(
    selectorActiveBankingAccount
  );
  const queryBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: fundingAccount?.id ?? "" },
      { enabled: !!fundingAccount?.id }
    );

  // Every work log in this run's pay period, scoped server-side via
  // `filter[payPeriodId][eq]`. The whole period feeds the "Work logs" tab; the
  // unapproved subset (derived below from this complete set) drives the alert.
  const payPeriodId = queryPayroll.data?.payPeriodId ?? undefined;
  const queryWorkLogs = queriesWorkLogs.listWorkLogs.useQueryAll(
    { filter: { payPeriodId: { eq: payPeriodId as string } } },
    { enabled: !!payPeriodId }
  );

  return (
    <WSQueries queries={{ queryPayroll }}>
      {({ queryPayrollData }) => {
        const payrollRun = queryPayrollData;
        const payrollRunNumber = selectorPayrollRunNumber(payrollRun);
        const amounts = selectorPayrollRunBreakdownAmounts(payrollRun);
        const worklogApproval = selectorPayrollRunWorklogApproval(payrollRun);
        const approvalSteps = selectorPayrollRunApprovalSteps(
          payrollRun.status
        );
        // Which run actions apply, from the mapped status. Finalize/cancel only
        // make sense before the run is finalized; delete only for an untouched
        // draft.
        const isBeforeFinalize =
          payrollRun.status === PayrollRunStatus.DRAFT ||
          payrollRun.status === PayrollRunStatus.PENDING_APPROVALS;
        const canFinalize = isBeforeFinalize;
        const canCancel = isBeforeFinalize;
        const canDelete = payrollRun.status === PayrollRunStatus.DRAFT;
        // The run still draws from the wallet balance until it's been paid or
        // failed/cancelled.
        const isPayable =
          payrollRun.status !== PayrollRunStatus.PAID &&
          payrollRun.status !== PayrollRunStatus.CANCELLED &&
          payrollRun.status !== PayrollRunStatus.FAILED;

        const walletBalance = selectorPayrollRunsWalletBalance({
          internalAccount: fundingAccount,
          balance: queryBalance.data
        });
        const hasInsufficientBalance =
          isPayable &&
          walletBalance.availableBalance < (amounts.liabilityAmount ?? 0);

        const payPeriodWorkLogs = queryWorkLogs.flatData ?? [];
        const unapprovedWorkLogs = payPeriodWorkLogs.filter(
          workLog => workLog.status === WorkLogStatus.IN_REVIEW
        );
        const handleBulkReviewWorkLogs = () => {
          const firstUnapproved = unapprovedWorkLogs[0];
          if (firstUnapproved?.id) {
            goToWorkLogDetails(
              { workLogId: firstUnapproved.id },
              { search: { bulkReview: "true" } }
            );
          }
        };

        const modalContext = {
          payrollRunId,
          payrollNumber: payrollRunNumber
        };

        const handleFinalize = () => modalFinalizePayroll.open(modalContext);
        const handleCancel = () => modalCancelPayroll.open(modalContext);
        const handleDelete = async () => {
          // The run no longer exists after a delete, so leave the detail page.
          const deleted = await modalDeletePayroll.open(modalContext);
          if (deleted) {
            props.onDeleted();
          }
        };

        const toolbarActions: ToolbarAction[] = [];
        if (canFinalize) {
          toolbarActions.push({
            label: "Finalize now",
            buttonKind: "Secondary",
            onClick: handleFinalize
          });
        }
        toolbarActions.push({ separator: true });
        if (canCancel) {
          toolbarActions.push({
            label: "Cancel payroll run",
            onClick: handleCancel
          });
        }
        if (canDelete) {
          toolbarActions.push({
            label: "Delete payroll run",
            icon: "trash",
            onClick: handleDelete
          });
        }
        // No run-level export endpoint exists on `PayrollRunsApi` yet (only
        // per-pay-statement PDFs), so no "Download summary" action is offered.
        toolbarActions.push({
          label: "Manage payroll settings",
          icon: "settings",
          onClick: () => history.push(PATH_SETTINGS_PAYROLL)
        });

        return (
          <WSPage>
            <WSPageToolbar
              actions={toolbarActions}
              breadcrumbs={[
                ...props.breadcrumbs,
                { label: `Payroll run #${payrollRunNumber}` }
              ]}
              title={`Payroll run #${payrollRunNumber} · ${formatPayPeriodLabel(
                payrollRun.payPeriodStartDate
              )}`}
              titleAfter={
                payrollRun.cycleType === PayrollRunCycleType.OFF_CYCLE ? (
                  <WSPill badge text="Off-cycle" theme="neutral" />
                ) : undefined
              }
            />

            {hasInsufficientBalance && (
              <WSAlert
                icon="alert"
                mt="L"
                theme="warning"
                title="Insufficient balance"
              >
                {`Your Wingspan wallet balance (${formatMoney(
                  walletBalance.availableBalance,
                  PAYROLL_CURRENCY
                )}) is below this run's liability amount (${formatMoney(
                  amounts.liabilityAmount ?? 0,
                  PAYROLL_CURRENCY
                )}). Top up your wallet before this run is finalized.`}
              </WSAlert>
            )}

            {worklogApproval.dueDate && (
              <WSAlert
                action={
                  unapprovedWorkLogs.length
                    ? {
                        text: "Bulk review worklogs",
                        onClick: handleBulkReviewWorkLogs
                      }
                    : undefined
                }
                icon="alert"
                mt="L"
                theme="warning"
                title="Ongoing worklog approvals."
              >
                {`${unapprovedWorkLogs.length} unapproved worklog${
                  unapprovedWorkLogs.length === 1 ? "" : "s"
                } for this pay period. Review and approve worklogs by ${toWSDateString(
                  worklogApproval.dueDate,
                  "monthDayYear"
                )}. You have ${worklogApproval.daysLeft} days left. Any unapproved worklogs won't be included in this payroll run.`}
              </WSAlert>
            )}

            <WSGrid gutter="L" mt="XL">
              <WSGrid.Item span={{ s: "12", l: "8" }}>
                <WSElement>
                  <WSSectionToolbar title="Payroll run details" />
                  <WSElement mt="M">
                    <PayrollRunDetailsCard payrollRun={payrollRun} />
                  </WSElement>
                </WSElement>
              </WSGrid.Item>

              <WSGrid.Item span={{ s: "12", l: "4" }}>
                <WSList gap="L">
                  <ApprovalWindowWidget steps={approvalSteps} />
                  <WalletBalanceCard
                    balance={walletBalance}
                    onManageAccount={() => history.push(PATH_WALLET)}
                    onTopUp={() => history.push(PATH_WALLET)}
                  />
                  <ResourceHistory
                    resources={[payrollRunResource]}
                    onViewAll={() =>
                      modalResourceDrillDownHistory.open({
                        resource: payrollRunResource,
                        title: "Payroll run history"
                      })
                    }
                  />
                </WSList>
              </WSGrid.Item>
            </WSGrid>

            <WSElement mt="2XL">
              <WSTabs
                tabs={[
                  {
                    label: "Work logs",
                    render: () => (
                      <WSElement mt="M">
                        <TableRunWorkLogs
                          loading={queryWorkLogs.isLoading}
                          workLogs={payPeriodWorkLogs}
                          onRowOpen={workLogId =>
                            goToWorkLogDetails({ workLogId })
                          }
                        />
                      </WSElement>
                    )
                  },
                  {
                    label: "Paystubs",
                    render: () => (
                      <WSElement mt="M">
                        <FilterableTablePaystubs
                          payrollRunId={payrollRunId}
                          onRowOpen={paystubId =>
                            goToPaystub({
                              // The run's own year, not the dashboard's — the
                              // screen is route-agnostic, and this is the year
                              // the dashboard would have listed it under
                              // anyway (same rule as the details links).
                              year: getPayrollRunYear(payrollRun),
                              payrollRunId,
                              paystubId
                            })
                          }
                        />
                      </WSElement>
                    )
                  }
                ]}
              />
            </WSElement>
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
